import React from 'react'
import { AnimatePresence, motion, useReducedMotion, type Transition } from 'framer-motion'
import './NewTaskDropGap.scss'

/** Wie die Zeilen beim Verschieben weichen (`SortableTaskEntry`): 300ms, dieselbe Kurve. */
const SHIFT = { duration: 0.3, ease: [0.25, 1, 0.5, 1] as const }
/** Ausblenden beim Schließen — so lange, wie die Lücke mit dieser Kurve noch sichtbar Höhe hat. */
const FADE = { duration: 0.15, ease: 'easeOut' as const }

/**
 * Die Bewegung der Lücke. Sie schließt mit der Kurve, mit der die Zeilen weichen: anfangs sehr schnell,
 * dann ein langes Auslaufen. Die letzten ~150ms ist sie unter 2px hoch — getönt stand dort eine
 * Haarlinie (gemessen 10.10.2026: 1,95 → 0,03px über neun Bilder, gemeldet von Sven). Beim Weichen der
 * Zeilen sieht man das nicht, bei einer Fläche schon. Deshalb blendet sie beim Schließen zugleich aus.
 */
export function newTaskGapMotion({ height, flexGap, offset, reduced }: { height: number; flexGap: number; offset: number; reduced: boolean }) {
  const closed = { height: 0, marginTop: -flexGap }
  const transition: Transition = reduced ? { duration: 0 } : { ...SHIFT, opacity: FADE }
  return {
    initial: { ...closed, opacity: 1 },
    animate: { height, marginTop: offset, opacity: 1 },
    exit: { ...closed, opacity: 0 },
    transition,
  }
}

export interface NewTaskDropGapProps {
  /** Die Lücke ist das Ziel unter dem Zeiger. */
  open: boolean
  /** Höhe der offenen Lücke — die einer Zeile, gemessen beim Zugbeginn. */
  height: number
  /**
   * Abstand der Liste, den eine geschlossene Lücke schlucken muss (`.task-list { gap: 2px }`). Ohne
   * ihn stünde beim Öffnen und Schließen ein Sprung von 2px.
   */
  flexGap?: number
  /** Abstand nach oben, solange sie offen ist — im leeren Abschnitt 12px wie die Eingabe dort. */
  offset?: number
}

/**
 * **Die Lücke beim Ziehen von „Neue Aufgabe“** (#301): getönt, so hoch wie eine Zeile. Sie steht im
 * Fluss der Liste — die Zeilen darunter weichen dadurch von selbst, wie beim Verschieben. Lässt man
 * los, entsteht an genau ihrer Stelle die Eingabe.
 *
 * Geschlossen ist sie gar nicht da; erst beim Öffnen wächst sie von 0 auf.
 *
 * @example
 * <NewTaskDropGap open={target === 'a'} height={34} flexGap={2} />
 */
export const NewTaskDropGap: React.FC<NewTaskDropGapProps> = ({ open, height, flexGap = 0, offset = 0 }) => {
  const reduced = useReducedMotion() ?? false
  const motionProps = newTaskGapMotion({ height, flexGap, offset, reduced })
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="gap"
          className="new-task-drop-gap"
          data-new-task-gap=""
          aria-hidden="true"
          {...motionProps}
        />
      )}
    </AnimatePresence>
  )
}
